{/* Copyright 2025 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../src/Layout';
export default Layout;
import docs from 'docs:@react-aria/utils';
import {FunctionAPI} from '../../src/FunctionAPI';

export const section = 'Utilities';
export const description = 'Converts a callback ref or object ref to an object ref.';

# useObjectRef

<PageDescription>{docs.exports.useObjectRef.description}</PageDescription>

## Introduction

`useObjectRef` is a utility function that will give an object ref back regardless of if a
callback ref or object ref was passed in. This is useful for passing refs to React Aria hooks.

## Example

```tsx render
'use client';
import React from 'react';
import {useObjectRef} from 'react-aria/useObjectRef';
import {useButton, type AriaButtonProps} from 'react-aria/useButton';

let Button = React.forwardRef((props: AriaButtonProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
  let objRef = useObjectRef(ref);
  let {buttonProps} = useButton(props, objRef);
  let {children} = props;

  return (
    <button {...buttonProps} ref={objRef}>
      {children}
    </button>
  );
});

function MyButton(props) {
  let ref = React.useRef(null);
  return <Button ref={ref} onPress={() => console.log(ref.current)}>{props.children}</Button>;
}

<MyButton>Test</MyButton>
```

## API

<FunctionAPI function={docs.exports.useObjectRef} links={docs.links} />